import React, { useState } from "react";
import Layout from "../../layout/Layout";
import { PlayCircle, CheckCircle } from "lucide-react";
function About() {
  const [openQuestion, setOpenQuestion] = useState(null);

  const toggleQuestion = (index) => {
    setOpenQuestion(openQuestion === index ? null : index);
  };

  const faqs = [
    {
      question: "How do I make a reservation on your website?",
      answer:
        "Provide a step-by-step guide on how users can browse and book travel services on your platform. Include information on searching for destinations, selecting dates, choosing accommodation, and completing the booking process. Mention any special features or tools that can help users find the best deals.",
    },
    {
      question:
        "What documents do I need for my trip, and how do I obtain them?",
      answer:
        "Outline the necessary travel documents, such as passports, visas, and identification, and offer instructions on how to acquire them. Include tips for international travel and resources for country-specific requirements.",
    },
    {
      question:
        "In the event that I need to modify or cancel my reservation, what are the policies in place?",
      answer:
        "Detail the modification and cancellation policies, including any fees, deadlines, or conditions. Explain the process for submitting a request and provide contact information for customer support.",
    },
    {
      question:
        "Can you specify the types of credit/debit cards, digital wallets, or other online payment methods accepted?",
      answer:
        "List the accepted payment methods and currencies, and provide details about secure payment processing. Address any concerns related to transaction security and privacy.",
    },
    {
      question:
        "What are the working hours, and what can I expect in terms of response times?",
      answer:
        "Clarify the customer support availability, including business hours and different communication channels (e.g., live chat, email, phone). Set clear expectations for response times to user inquiries.",
    },
  ];

  return (
    <Layout>
      <div
        className="w-full min-h-[60vh] md:min-h-[80vh] lg:min-h-screen bg-cover bg-center relative flex items-center"
        style={{ backgroundImage: "url('/about.jpg')" }}
      >
        <div className="absolute inset-0 bg-black opacity-10"></div>
        {/* Hero Section Content */}
        <div className="relative z-10 w-full flex items-center justify-center text-white py-12 px-4 md:px-10">
          <div className="max-w-2xl">
            <h1 className="text-3xl sm:text-4xl md:text-[55px] font-extrabold leading-tight">
              About Dalu Travel <br /> and Tourism
            </h1>
            <p className="mt-4 text-base sm:text-lg md:text-xl leading-relaxed">
              At Dalu Travel and Tourism, we believe every journey has a story that connects people, cultures, and nature.
              Our goal is to create meaningful travel experiences that inspire exploration and protect the beauty of our
              planet.
              Whether you want a relaxing getaway, a cultural adventure, or an eco-friendly trip, we design journeys that fit
              your interests. From stunning landscapes to hidden local gems, we make your travels authentic and
              unforgettable.
              With our experienced team and passion for responsible tourism, we help you discover places that bring joy to
              your heart and care for the world we live in.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center bg-gray-50 p-4 font-[Inter] mt-10 md:mt-20">
        {/* Container with rounded corners and shadow */}
        <div className="relative w-full max-w-4xl p-6 md:p-12 lg:p-16 min-h-[300px] md:min-h-[320px] lg:min-h-[340px] bg-white rounded-3xl shadow-2xl overflow-hidden">

          {/* Leaf image on the top-right, using an SVG for simplicity */}
          <div className="absolute top-0 right-0 z-0">
            <svg className="h-48 w-48 text-green-700 opacity-10" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
              <path d="M16.59 13.56c-.57.85-1.58 1.44-2.73 1.44-1.27 0-2.31-.76-2.79-1.85-.35-.8-.54-1.74-.54-2.75 0-2.88 1.76-5.26 4.31-6.14.7-.24 1.46-.37 2.22-.37.94 0 1.83.2 2.66.57.8.36 1.48.86 2.11 1.49l-1.35 1.35c-.47-.47-1.02-.85-1.63-1.14-.6-.29-1.28-.44-1.98-.44-.95 0-1.85.2-2.68.59-.8.37-1.5.87-2.12 1.49L12 9l-1.41-1.41c-.62.62-1.12 1.32-1.49 2.12-.39.83-.59 1.73-.59 2.68 0 .69.14 1.37.44 1.98.29.61.67 1.16 1.14 1.63L12 15l1.41 1.41c.62-.62 1.12-1.32 1.49-2.12.39-.83.59-1.73.59-2.68 0-.69-.14-1.37-.44-1.98-.29-.61-.67-1.16-1.14-1.63L12 9l-1.41-1.41c-.62.62-1.12 1.32-1.49 2.12-.39.83-.59 1.73-.59 2.68 0 .69.14 1.37.44 1.98.29.61.67 1.16 1.14 1.63L12 15z" />
            </svg>
          </div>

          {/* Content layout using a responsive grid */}
          <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 gap-8">

            {/* Our Mission section */}
            <div>
              <h2 className="text-4xl font-bold mb-4 text-gray-800">Our Mission</h2>
              <p className="text-lg text-gray-600">
                To revolutionize the travel business landscape by providing tailored solutions
                that enhance efficiency, profitability, and customer satisfaction.
              </p>
            </div>

            {/* Our Vision section */}
            <div>
              <h2 className="text-4xl font-bold mb-4 text-gray-800">Our Vision</h2>
              <p className="text-lg text-gray-600">
                To be the leading platform for travel entrepreneurs, fostering innovation and
                creating unparalleled opportunities for growth in the global travel industry.
              </p>
            </div>

          </div>
        </div>
      </div>

      <div className="bg-white text-gray-800 p-4 sm:p-8 md:p-16 antialiased">
        <div className="container mx-auto max-w-7xl">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8 md:gap-12">
            {/* Left Section: Image Grid */}
            <div className="grid grid-cols-2 gap-4 md:gap-6 w-full lg:w-1/2">
              <div className="col-span-1 row-span-2 relative group overflow-hidden rounded-3xl shadow-xl">
                <img
                  src="/welcome-to-dalu-1.png"
                  alt="Ancient temple and mountains"
                  className="w-full h-full object-cover transition-transform duration-300 transform group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center transition-opacity duration-300 opacity-100 group-hover:opacity-100">
                  <PlayCircle className="text-white text-opacity-80 w-16 h-16 transform transition-transform duration-300 group-hover:scale-110" />
                </div>
              </div>
              <div className="col-span-1 row-span-1 relative overflow-hidden rounded-3xl shadow-xl">
                <img
                  src="https://placehold.co/600x400/8d8d8d/ffffff?text=Friends+on+cliff"
                  alt="Two friends on a cliff edge"
                  className="w-full h-full object-cover transition-transform duration-300 transform group-hover:scale-105"
                />
              </div>
              <div className="col-span-1 row-span-1 relative overflow-hidden rounded-3xl shadow-xl">
                <img
                  src="https://placehold.co/600x400/8d8d8d/ffffff?text=Woman+on+beach"
                  alt="Woman raising arms on a beach"
                  className="w-full h-full object-cover transition-transform duration-300 transform group-hover:scale-105"
                />
              </div>
            </div>

            {/* Right Section: Content and Features */}
            <div className="w-full lg:w-1/2 lg:pl-8 xl:pl-16 mt-10 lg:mt-0">
              <div className="relative">
                <span className="inline-block bg-gray-100 text-gray-600 text-xs font-semibold px-3 py-1 rounded-full mb-4">
                  Easy payment
                </span>
                <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold leading-tight mb-4 tracking-tight">
                  We offer tours in a <br className="hidden md:inline" /> range
                  of locations
                </h1>
                <p className="text-gray-500 text-base sm:text-lg mb-8 max-w-lg">
                  Choose one style or create a package, fill your passports with
                  adventures together.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-8 mb-8 md:mb-12">
                  <div className="flex items-center text-gray-800">
                    <CheckCircle className="text-green-500 mr-2 w-5 h-5" />
                    <span>Security Assurance</span>
                  </div>
                  <div className="flex items-center text-gray-800">
                    <CheckCircle className="text-green-500 mr-2 w-5 h-5" />
                    <span>Customer Support</span>
                  </div>
                  <div className="flex items-center text-gray-800">
                    <CheckCircle className="text-green-500 mr-2 w-5 h-5" />
                    <span>Transparent Policies</span>
                  </div>
                  <div className="flex items-center text-gray-800">
                    <CheckCircle className="text-green-500 mr-2 w-5 h-5" />
                    <span>Reputable Affiliations</span>
                  </div>
                </div>

                <div className="text-sm text-gray-400 mb-4">
                  Payment is secure and safe
                </div>
                <div className="flex flex-wrap items-center gap-6">
                  <img
                    src="https://placehold.co/100x40/d7e5f0/808080?text=PayPal"
                    alt="PayPal"
                    className="h-8"
                  />
                  <img
                    src="https://placehold.co/100x40/d7e5f0/808080?text=Stripe"
                    alt="Stripe"
                    className="h-8"
                  />
                  <img
                    src="https://placehold.co/100x40/d7e5f0/808080?text=Mastercard"
                    alt="Mastercard"
                    className="h-8"
                  />
                  <img
                    src="https://placehold.co/100x40/d7e5f0/808080?text=Skrill"
                    alt="Skrill"
                    className="h-8"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-gray-100 text-gray-800 font-sans p-4 sm:p-8 md:p-16 antialiased">
        <div className="container mx-auto max-w-7xl">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8 md:gap-12">
            {/* Left Section: Single Prominent Image */}
            <div className="w-full lg:w-1/2 mb-6 lg:mb-0">
              <img
                src="/img-travel.png.png"
                alt="Dalu Travel Experience"
                className="w-full object-cover rounded-2xl"
              />
            </div>

            {/* Right Section: Content and Features */}
            <div className="grid grid-cols-2 gap-4 md:gap-6 w-full lg:w-1/2">
              <div className="col-span-1 row-span-2 relative group overflow-hidden rounded-3xl shadow-xl">
                <img
                  src="/welcome-to-dalu-1.png"
                  alt="Ancient temple and mountains"
                  className="w-full h-full object-cover transition-transform duration-300 transform group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center transition-opacity duration-300 opacity-100 group-hover:opacity-100">
                  <PlayCircle className="text-white text-opacity-80 w-16 h-16 transform transition-transform duration-300 group-hover:scale-110" />
                </div>
              </div>
              <div className="col-span-1 row-span-1 relative overflow-hidden rounded-3xl shadow-xl">
                <img
                  src="https://placehold.co/600x400/8d8d8d/ffffff?text=Friends+on+cliff"
                  alt="Two friends on a cliff edge"
                  className="w-full h-full object-cover transition-transform duration-300 transform group-hover:scale-105"
                />
              </div>
              <div className="col-span-1 row-span-1 relative overflow-hidden rounded-3xl shadow-xl">
                <img
                  src="https://placehold.co/600x400/8d8d8d/ffffff?text=Woman+on+beach"
                  alt="Woman raising arms on a beach"
                  className="w-full h-full object-cover transition-transform duration-300 transform group-hover:scale-105"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-center items-center min-h-[60vh] md:min-h-[80vh] lg:min-h-screen bg-rose-50 px-2 sm:px-4 py-8 sm:py-12 font-sans">
        <div className="flex flex-col lg:flex-row bg-white rounded-3xl shadow-2xl p-4 sm:p-8 lg:p-16 max-w-7xl w-full">
          {/* Left Floating Elements Section */}
          <div className="relative w-full lg:w-1/2 flex items-center justify-center p-4 sm:p-8 lg:p-0 min-h-[300px]">
            <div
              className="absolute w-40 h-40 sm:w-64 sm:h-64 lg:w-80 lg:h-80 rounded-full bg-cover bg-center overflow-hidden -top-8 sm:-top-12 lg:top-0 left-1/2 lg:left-0 -translate-x-1/2 lg:translate-x-0 z-10"
              style={{ backgroundImage: "url('/welcome-to-dalu-1.png')" }}
            >
              {/* The image is a placeholder */}
            </div>
            {/* Customer Service Card */}
            <div className="absolute top-12 sm:top-16 lg:top-16 left-1/2 lg:left-12 -translate-x-1/2 lg:translate-x-0 bg-white p-2 sm:p-3 rounded-xl shadow-lg transform rotate-3 z-20">
              <div className="flex items-center space-x-2">
                <span className="text-xs sm:text-sm font-semibold text-gray-700 whitespace-nowrap">
                  Customer Service
                </span>
              </div>
              <div className="mt-1 text-xs sm:text-sm font-bold text-gray-900 whitespace-nowrap">
                +01 235 368
              </div>
            </div>
            {/* Flight Tickets Card */}
            <div className="absolute top-32 sm:top-48 lg:top-48 left-1/4 lg:left-0 -translate-x-1/2 lg:translate-x-0 bg-white p-2 sm:p-3 rounded-xl shadow-lg transform -rotate-6 z-20">
              <div className="flex items-center space-x-2">
                <span className="text-xs sm:text-sm font-semibold text-gray-700">
                  Flight Tickets
                </span>
              </div>
            </div>
            {/* Tour Guide Card */}
            <div className="absolute top-28 sm:top-36 lg:top-36 right-1/4 lg:right-0 -translate-x-1/2 lg:translate-x-0 bg-white p-2 sm:p-3 rounded-xl shadow-lg transform rotate-12 z-20">
              <div className="flex items-center space-x-2">
                <span className="text-xs sm:text-sm font-semibold text-gray-700 z-20">
                  Tour Guide
                </span>
              </div>
              <div
                className="absolute w-8 h-8 sm:w-20 sm:h-20 lg:w-40 lg:h-40 rounded-full bg-cover bg-center overflow-hidden -translate-x-1/2 lg:translate-x-0 "
                style={{ backgroundImage: "url('/welcome-to-dalu-1.png')" }}
              >
                {/* The image is a placeholder */}
              </div>
            </div>
            {/* Suitcase Placeholder */}
            <div className="absolute bottom-0 lg:bottom-60 left-1/2 lg:left-1/4 -translate-x-1/2 lg:translate-x-0 transform rotate-12 z-60">
              <div
                className="absolute w-24 h-24 sm:w-40 sm:h-40 lg:w-50 lg:h-50 rounded-full bg-cover bg-center overflow-hidden -top-8 sm:-top-12 lg:top-0 left-1/2 lg:left-0 -translate-x-1/2 lg:translate-x-0 z-10"
                style={{ backgroundImage: "url('/welcome-to-dalu-1.png')" }}
              >
                {/* The image is a placeholder */}
              </div>
              <div className="w-16 h-12 sm:w-24 sm:h-20 bg-amber-800 rounded-lg shadow-xl"></div>
              <div className="w-6 h-2 sm:w-8 sm:h-4 bg-amber-600 rounded-b-md mx-auto -mt-2"></div>
              <div className="w-2 h-8 sm:w-4 sm:h-12 bg-gray-500 rounded-full absolute -top-4 sm:-top-8 left-1/2 -translate-x-1/2"></div>
              <div className="w-12 h-8 sm:w-20 sm:h-16 rounded-full bg-lime-500/50 blur-3xl absolute -top-4 sm:-top-8 -left-2 sm:-left-4 -z-10"></div>
            </div>
          </div>
          {/* Right Content Section */}
          <div className="w-full lg:w-1/2 lg:pl-8 xl:pl-16 mt-10 lg:mt-0">
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 tracking-tight">
              You will love us
            </h1>
            <p className="text-base sm:text-lg text-gray-600 mb-8 md:mb-12">
              Because more than 268 other customers have loved us too
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8">
              {/* Feature Card 1 */}
              <div className="bg-white p-4 sm:p-6 rounded-2xl border border-gray-100 shadow-md">
                <div className="flex items-center justify-center w-8 h-8 sm:w-12 sm:h-12 rounded-xl bg-orange-200">
                  <span className="text-xl sm:text-2xl" role="img" aria-label="shield">
                    🛡️
                  </span>
                </div>
                <h3 className="text-lg sm:text-xl font-semibold mt-4 mb-2 text-gray-900">
                  Security Assurance
                </h3>
                <p className="text-xs sm:text-sm text-gray-600 mb-2 sm:mb-4">
                  We care about your safety and privacy. All your data and payments are protected through advanced
                  encryption and secure systems — ensuring a worry-free travel experience.
                </p>
                <a
                  href="#"
                  className="flex items-center text-xs sm:text-sm font-medium text-purple-700 hover:text-purple-800 transition-colors"
                >
                  Learn More
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 ml-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M14 5l7 7m0 0l-7 7m7-7H3"
                    />
                  </svg>
                </a>
              </div>
              {/* Feature Card 2 */}
              <div className="bg-white p-4 sm:p-6 rounded-2xl border border-gray-100 shadow-md">
                <div className="flex items-center justify-center w-8 h-8 sm:w-12 sm:h-12 rounded-xl bg-red-200">
                  <span className="text-xl sm:text-2xl" role="img" aria-label="headphones">
                    🎧
                  </span>
                </div>
                <h3 className="text-lg sm:text-xl font-semibold mt-4 mb-2 text-gray-900">
                  Customer Support
                </h3>
                <p className="text-xs sm:text-sm text-gray-600 mb-2 sm:mb-4">
                  Our dedicated support team is here for you 24/7. Whether you need help before, during, or after your trip,
                  we’re just a message or call away — ready to assist anytime.
                </p>
                <a
                  href="#"
                  className="flex items-center text-xs sm:text-sm font-medium text-purple-700 hover:text-purple-800 transition-colors"
                >
                  Learn More
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 ml-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M14 5l7 7m0 0l-7 7m7-7H3"
                    />
                  </svg>
                </a>
              </div>
              {/* Feature Card 3 */}
              <div className="bg-white p-4 sm:p-6 rounded-2xl border border-gray-100 shadow-md">
                <div className="flex items-center justify-center w-8 h-8 sm:w-12 sm:h-12 rounded-xl bg-blue-200">
                  <span className="text-xl sm:text-2xl" role="img" aria-label="document">
                    📄
                  </span>
                </div>
                <h3 className="text-lg sm:text-xl font-semibold mt-4 mb-2 text-gray-900">
                  Transparent Policies
                </h3>
                <p className="text-xs sm:text-sm text-gray-600 mb-2 sm:mb-4">
                  We believe in honesty and clarity. Our pricing, booking, and refund policies are straightforward and easy to
                  understand — so you always know exactly what to expect.
                </p>
                <a
                  href="#"
                  className="flex items-center text-xs sm:text-sm font-medium text-purple-700 hover:text-purple-800 transition-colors"
                >
                  Learn More
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 ml-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M14 5l7 7m0 0l-7 7m7-7H3"
                    />
                  </svg>
                </a>
              </div>
              {/* Feature Card 4 */}
              <div className="bg-white p-4 sm:p-6 rounded-2xl border border-gray-100 shadow-md">
                <div className="flex items-center justify-center w-8 h-8 sm:w-12 sm:h-12 rounded-xl bg-cyan-200">
                  <span className="text-xl sm:text-2xl" role="img" aria-label="chart">
                    📊
                  </span>
                </div>
                <h3 className="text-lg sm:text-xl font-semibold mt-4 mb-2 text-gray-900">
                  Reputable Affiliations
                </h3>
                <p className="text-xs sm:text-sm text-gray-600 mb-2 sm:mb-4">
                  We’re proud to partner with trusted global travel networks and eco-friendly organizations. Our affiliations
                  reflect our promise to deliver reliable, responsible, and high-quality travel services.
                </p>
                <a
                  href="#"
                  className="flex items-center text-xs sm:text-sm font-medium text-purple-700 hover:text-purple-800 transition-colors"
                >
                  Learn More
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 ml-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M14 5l7 7m0 0l-7 7m7-7H3"
                    />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Placeholder Plane Icons (simplified) */}
    </Layout>
  );
}

export default About;
